import type { LotCostEvent } from '../types/production'

import { ensureOfflineReady } from '../offline/initOffline'
import { getActiveTenantKey } from '../offline/tenant'
import { enqueueEntityChange, enqueueEntityChanges } from '../offline/outbox/entityOutbox'
import { getOfflineDb } from '../offline/dbFactory'
import {
  dexieLotCostEventRepo,
  type LotCostEventListParams
} from '../offline/repos/DexieLotCostEventRepo'
import { normalizeToIsoZ } from './dateNormalization'
import {
  buildQueuedEntityMutationResult,
  readEntityBatchSyncStatus,
  retryEntityBatchSyncStatusWithReport,
  type EntityBatchSyncRetryReport,
  type EntityBatchSyncStatus,
  type EntityMutationResult
} from './entityMutationSync'
import { createId } from './id'
import { api } from './api'

const nowIso = (): string => new Date().toISOString()

const toOptionalTrimmed = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed || undefined
}

export const CATEGORY_PREFIXES = ['G', 'N', 'H', 'L', 'I', 'M', 'A', 'F', 'R'] as const
export type CategoryPrefix = typeof CATEGORY_PREFIXES[number]

const EXPENSE_NATURES = ['fixed', 'variable'] as const
export type ExpenseNature = typeof EXPENSE_NATURES[number]

const COST_CLASSIFICATIONS = ['direct', 'indirect'] as const
export type CostClassification = typeof COST_CLASSIFICATIONS[number]

// Defaults used by the manual-entry form in T3-B-03c AND by the auto-derivation
// helper in T3-B-03b so both share one source of truth.
export const DEFAULT_NATURE_BY_PREFIX: Record<CategoryPrefix, ExpenseNature> = {
  G: 'variable', N: 'variable', H: 'variable', L: 'variable',
  I: 'fixed',    M: 'variable', A: 'fixed',    F: 'fixed',    R: 'fixed'
}

export const DEFAULT_CLASSIFICATION_BY_PREFIX: Record<CategoryPrefix, CostClassification> = {
  G: 'direct',   N: 'direct',   H: 'direct',   L: 'direct',
  I: 'indirect', M: 'indirect', A: 'indirect', F: 'indirect', R: 'indirect'
}

export const PENDING_CATEGORY_CODE = 'PENDING'

const normalizeCategoryPrefix = (value: unknown): CategoryPrefix => {
  if (typeof value === 'string' && (CATEGORY_PREFIXES as readonly string[]).includes(value)) {
    return value as CategoryPrefix
  }
  throw new Error(`Category prefix must be one of ${CATEGORY_PREFIXES.join(' | ')}`)
}

const normalizeExpenseNature = (value: unknown): ExpenseNature => {
  if (typeof value === 'string' && (EXPENSE_NATURES as readonly string[]).includes(value)) {
    return value as ExpenseNature
  }
  throw new Error("Expense nature must be 'fixed' or 'variable'")
}

const normalizeCostClassification = (value: unknown): CostClassification => {
  if (typeof value === 'string' && (COST_CLASSIFICATIONS as readonly string[]).includes(value)) {
    return value as CostClassification
  }
  throw new Error("Cost classification must be 'direct' or 'indirect'")
}

const normalizeAmountCents = (value: unknown): number => {
  const raw = typeof value === 'number' ? value : Number(String(value ?? '').replaceAll(',', '').trim())
  if (!Number.isFinite(raw)) {
    throw new Error('Amount must be numeric')
  }
  if (!Number.isInteger(raw)) {
    throw new Error('Amount must be a whole number of cents')
  }
  if (raw < 0) {
    throw new Error('Amount must be zero or greater')
  }
  if (raw > 100_000_000_000) {
    throw new Error('Amount must be 100,000,000,000 cents or below')
  }
  return raw
}

const normalizeCurrency = (value: unknown): string => {
  if (typeof value !== 'string') throw new Error('Currency is required')
  const trimmed = value.trim().toUpperCase()
  if (!/^[A-Z]{3}$/.test(trimmed)) throw new Error('Currency must be a 3-letter ISO code')
  return trimmed
}

const normalizeRecordedAt = (raw?: string): string => {
  if (!raw) return nowIso()
  const normalized = normalizeToIsoZ(raw)
  if (normalized.ok) return normalized.isoZ
  return nowIso()
}

const isOnline = (): boolean => {
  if (typeof navigator === 'undefined') return true
  return navigator.onLine
}

const REMOTE_LIST_TTL_MS = 30_000
const lastRemoteListAtByKey = new Map<string, number>()

const buildRemoteListKey = (params: LotCostEventListParams): string => {
  const clean = {
    tenantKey: getActiveTenantKey(),
    farmId: params.farmId?.trim() || '',
    productionUnitId: params.productionUnitId?.trim() || '',
    lotId: params.lotId?.trim() || '',
    categoryPrefix: params.categoryPrefix?.trim() || '',
    costClassification: params.costClassification?.trim() || '',
    sourceEventType: params.sourceEventType?.trim() || '',
    sourceEventId: params.sourceEventId?.trim() || '',
    fromRecordedAt: params.fromRecordedAt?.trim() || '',
    toRecordedAt: params.toRecordedAt?.trim() || ''
  }
  return JSON.stringify(clean)
}

const shouldAttemptRemoteList = (params: LotCostEventListParams): boolean => {
  if (!isOnline()) return false
  const key = buildRemoteListKey(params)
  const lastAt = lastRemoteListAtByKey.get(key)
  if (!lastAt) return true
  return Date.now() - lastAt > REMOTE_LIST_TTL_MS
}

const getPendingLotCostEventIds = async (): Promise<Set<string>> => {
  const entries = await getOfflineDb().entityOutbox.toArray()
  return new Set(
    entries
      .filter((entry) => entry.entityType === 'lotCostEvent')
      .map((entry) => entry.entityId)
  )
}

const hasPendingLotCostEventOutboxEntry = async (entityId: string): Promise<boolean> => {
  const trimmed = entityId.trim()
  if (!trimmed) return false

  try {
    const db = getOfflineDb()
    const match = await db.entityOutbox
      .where('[entityType+entityId]')
      .equals(['lotCostEvent', trimmed])
      .first()
    return Boolean(match)
  } catch {
    return false
  }
}

const pruneRemovedRemoteLotCostEvents = async (
  cached: LotCostEvent[],
  remoteMapped: LotCostEvent[]
): Promise<void> => {
  const remoteIds = new Set(remoteMapped.map((event) => event.id))

  for (const event of cached) {
    if (remoteIds.has(event.id)) continue
    // Preserve local-only changes until their outbox entry is flushed.
    // eslint-disable-next-line no-await-in-loop
    const hasPending = await hasPendingLotCostEventOutboxEntry(event.id)
    if (hasPending) continue
    // eslint-disable-next-line no-await-in-loop
    await dexieLotCostEventRepo.delete(event.id)
  }
}

/** Facility-wide costs have no lot (`lotId: ''` on the client) and carry `metadata.scope = 'facility'`. */
export const FACILITY_COST_SCOPE = 'facility'

export const isFacilityCostMetadata = (metadata: unknown): boolean =>
  Boolean(metadata) && typeof metadata === 'object' && (metadata as Record<string, unknown>).scope === FACILITY_COST_SCOPE

export const isFacilityCost = (event: Pick<LotCostEvent, 'lotId' | 'metadata'>): boolean =>
  !event.lotId && isFacilityCostMetadata(event.metadata)

export const normalizeRemoteLotCostEvent = (candidate: any): LotCostEvent | null => {
  if (!candidate || typeof candidate !== 'object') return null
  const id = typeof candidate.id === 'string' ? candidate.id.trim() : ''
  const farmId = typeof candidate.farmId === 'string' ? candidate.farmId.trim() : ''
  const lotId = typeof candidate.lotId === 'string' ? candidate.lotId.trim() : ''
  const recordedAtRaw = typeof candidate.recordedAt === 'string' ? candidate.recordedAt.trim() : ''
  // A cost with no lot is kept only when it is a facility-wide cost (findings #100);
  // a cost whose lot was deleted stays out, as before.
  if (!id || !farmId || !recordedAtRaw) return null
  if (!lotId && !isFacilityCostMetadata(candidate.metadata)) return null

  try {
    const categoryPrefix = normalizeCategoryPrefix(candidate.categoryPrefix)
    const expenseNature =
      candidate.expenseNature !== undefined && candidate.expenseNature !== null
        ? normalizeExpenseNature(candidate.expenseNature)
        : DEFAULT_NATURE_BY_PREFIX[categoryPrefix]
    const costClassification =
      candidate.costClassification !== undefined && candidate.costClassification !== null
        ? normalizeCostClassification(candidate.costClassification)
        : DEFAULT_CLASSIFICATION_BY_PREFIX[categoryPrefix]

    const categorySequence =
      typeof candidate.categorySequence === 'number' && Number.isInteger(candidate.categorySequence)
        ? candidate.categorySequence
        : 0
    const categoryCode =
      typeof candidate.categoryCode === 'string' && candidate.categoryCode.trim()
        ? candidate.categoryCode.trim()
        : PENDING_CATEGORY_CODE

    return {
      id,
      farmId,
      lotId,
      productionUnitId:
        typeof candidate.productionUnitId === 'string'
          ? candidate.productionUnitId.trim() || undefined
          : undefined,
      recordedAt: normalizeRecordedAt(recordedAtRaw),
      categoryPrefix,
      categorySequence,
      categoryCode,
      expenseNature,
      costClassification,
      amountCents: normalizeAmountCents(candidate.amountCents),
      currency: normalizeCurrency(candidate.currency),
      sourceEventType: toOptionalTrimmed(candidate.sourceEventType),
      sourceEventId: toOptionalTrimmed(candidate.sourceEventId),
      notes: toOptionalTrimmed(candidate.notes),
      metadata:
        typeof candidate.metadata === 'object' && candidate.metadata !== null ? candidate.metadata : undefined,
      createdAt: typeof candidate.createdAt === 'string' ? candidate.createdAt : undefined,
      updatedAt: typeof candidate.updatedAt === 'string' ? candidate.updatedAt : undefined
    }
  } catch {
    return null
  }
}

export type LotCostEventCreatePayload = {
  id?: string
  farmId: string
  lotId: string
  productionUnitId?: string
  recordedAt?: string
  categoryPrefix: CategoryPrefix
  // Optional on the payload — client auto-fills from DEFAULT_*_BY_PREFIX if omitted.
  expenseNature?: ExpenseNature
  costClassification?: CostClassification
  amountCents: number | string
  currency: string
  sourceEventType?: string
  sourceEventId?: string
  notes?: string
  metadata?: Record<string, unknown>
}

export type LotCostEventUpdatePayload = Partial<{
  productionUnitId: string | null
  recordedAt: string
  expenseNature: ExpenseNature
  costClassification: CostClassification
  amountCents: number | string
  currency: string
  sourceEventType: string | null
  sourceEventId: string | null
  notes: string | null
  metadata: Record<string, unknown> | null
}>

export type LotCostEventMutationResult = EntityMutationResult<LotCostEvent>
export type LotCostEventDeleteResult = EntityMutationResult<void>

const normalizeLotCostEventRecord = (args: {
  candidate: LotCostEventCreatePayload | LotCostEvent
  id?: string
  createdAt?: string
  updatedAt?: string
}): LotCostEvent => {
  const farmId = toOptionalTrimmed(args.candidate.farmId)
  if (!farmId) throw new Error('Facility is required')

  const lotId = toOptionalTrimmed(args.candidate.lotId) ?? ''
  if (!lotId && !isFacilityCostMetadata(args.candidate.metadata)) throw new Error('Lot is required')

  const sourceEventType = toOptionalTrimmed(args.candidate.sourceEventType)
  const sourceEventId = toOptionalTrimmed(args.candidate.sourceEventId)
  if ((sourceEventType && !sourceEventId) || (!sourceEventType && sourceEventId)) {
    throw new Error('sourceEventType and sourceEventId must be provided together')
  }

  const timestamp = nowIso()
  const candidateAsEvent = args.candidate as LotCostEvent
  const candidateId =
    typeof candidateAsEvent.id === 'string' ? candidateAsEvent.id.trim() : ''

  const categoryPrefix = normalizeCategoryPrefix(args.candidate.categoryPrefix)
  const expenseNature =
    args.candidate.expenseNature !== undefined && args.candidate.expenseNature !== null
      ? normalizeExpenseNature(args.candidate.expenseNature)
      : DEFAULT_NATURE_BY_PREFIX[categoryPrefix]
  const costClassification =
    args.candidate.costClassification !== undefined && args.candidate.costClassification !== null
      ? normalizeCostClassification(args.candidate.costClassification)
      : DEFAULT_CLASSIFICATION_BY_PREFIX[categoryPrefix]

  // Round-trip server-computed accounting identifiers when re-normalizing an
  // already-persisted event (e.g. imported logs); otherwise seed the offline
  // placeholders that get replaced by the next remote list().
  const candidateSequence = candidateAsEvent.categorySequence
  const categorySequence =
    typeof candidateSequence === 'number' && Number.isInteger(candidateSequence)
      ? candidateSequence
      : 0
  const candidateCode = candidateAsEvent.categoryCode
  const categoryCode =
    typeof candidateCode === 'string' && candidateCode.trim()
      ? candidateCode.trim()
      : PENDING_CATEGORY_CODE

  return {
    id: args.id?.trim() || candidateId || createId('lce'),
    farmId,
    lotId,
    productionUnitId: toOptionalTrimmed(args.candidate.productionUnitId),
    recordedAt: normalizeRecordedAt(args.candidate.recordedAt),
    categoryPrefix,
    categorySequence,
    categoryCode,
    expenseNature,
    costClassification,
    amountCents: normalizeAmountCents(args.candidate.amountCents),
    currency: normalizeCurrency(args.candidate.currency),
    sourceEventType,
    sourceEventId,
    notes: toOptionalTrimmed(args.candidate.notes),
    metadata: args.candidate.metadata,
    createdAt: args.createdAt?.trim() || timestamp,
    updatedAt: args.updatedAt?.trim() || timestamp
  }
}

// The server Zod create schema is `.strict()` — it rejects payloads that
// include `categorySequence` / `categoryCode` (server-computed accounting
// identifiers) or `createdAt` / `updatedAt` (server manages timestamps). Strip
// them from the outbox payload so drain works.
//
// Session 4 rehearsal 2026-07-07 blocker: leaving createdAt/updatedAt in the
// payload caused every LotCostEvent outbox POST to 400 silently (Zod strict
// rejection), leaving the auto-derived cost event in the client-side Dexie
// mirror but never propagating server-side. Cross-device sync broken.
const buildOutboxPayload = (log: LotCostEvent): Record<string, unknown> => {
  const {
    categorySequence: _seq,
    categoryCode: _code,
    createdAt: _createdAt,
    updatedAt: _updatedAt,
    ...serverPayload
  } = log
  if (!serverPayload.lotId) {
    // Facility-wide cost (findings #100): the server takes no lot at all.
    const { lotId: _lotId, productionUnitId: _unitId, ...facilityPayload } = serverPayload
    return { ...facilityPayload, id: log.id }
  }
  return { ...serverPayload, id: log.id }
}

export const lotCostEventApi = {
  readLotCostEventSyncStatus: async (eventIds: string[]): Promise<EntityBatchSyncStatus> => {
    return readEntityBatchSyncStatus('lotCostEvent', eventIds)
  },

  retryLotCostEventSyncStatusWithReport: async (
    eventIds: string[]
  ): Promise<EntityBatchSyncRetryReport> => {
    return retryEntityBatchSyncStatusWithReport({
      entityType: 'lotCostEvent',
      entityIds: eventIds
    })
  },

  list: async (
    params: LotCostEventListParams = {},
    options?: { preferLocal?: boolean; forceRemote?: boolean }
  ): Promise<LotCostEvent[]> => {
    await ensureOfflineReady()

    const [cached, pendingIds] = await Promise.all([
      dexieLotCostEventRepo.list(params),
      getPendingLotCostEventIds()
    ])
    const shouldFetchRemote = options?.forceRemote ? isOnline() : shouldAttemptRemoteList(params)

    // Same TTL-based pattern as fishStockLogApi / weightSampleLogApi
    // (finding #57): `preferLocal` remains in the signature for back-compat
    // but freshness is decided by the `REMOTE_LIST_TTL_MS` gate so
    // peer-device events surface after the TTL window even when the caller
    // opted in to prefer-local.
    if (!shouldFetchRemote) {
      return cached
    }

    const key = buildRemoteListKey(params)
    try {
      const remote = await api.lotCostEvents.list({
        farmId: params.farmId,
        productionUnitId: params.productionUnitId,
        lotId: params.lotId,
        categoryPrefix: params.categoryPrefix,
        costClassification: params.costClassification,
        sourceEventType: params.sourceEventType,
        sourceEventId: params.sourceEventId,
        fromRecordedAt: params.fromRecordedAt,
        toRecordedAt: params.toRecordedAt,
        noCache: options?.forceRemote
      })

      const mapped = remote
        .map(normalizeRemoteLotCostEvent)
        .filter((event): event is LotCostEvent => Boolean(event))
        .filter((event) => !pendingIds.has(event.id))

      await pruneRemovedRemoteLotCostEvents(cached, mapped)
      await dexieLotCostEventRepo.upsertMany(mapped)
      lastRemoteListAtByKey.set(key, Date.now())

      return dexieLotCostEventRepo.list(params)
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[lotCostEventApi] remote list failed; falling back to cache', error)
      }
      if (cached.length > 0) return cached
      throw error
    }
  },

  get: async (id: string): Promise<LotCostEvent | undefined> => {
    await ensureOfflineReady()
    return dexieLotCostEventRepo.getById(id)
  },

  create: async (payload: LotCostEventCreatePayload): Promise<LotCostEventMutationResult> => {
    await ensureOfflineReady()
    const timestamp = nowIso()
    const event = normalizeLotCostEventRecord({
      candidate: payload,
      id: payload.id,
      createdAt: timestamp,
      updatedAt: timestamp
    })

    await dexieLotCostEventRepo.upsert(event)
    await enqueueEntityChange({
      entityType: 'lotCostEvent',
      op: 'upsert',
      entityId: event.id,
      payload: buildOutboxPayload(event)
    })

    return buildQueuedEntityMutationResult(event, {
      entityType: 'lotCostEvent',
      entityId: event.id
    })
  },

  saveImportedLogs: async (logs: LotCostEvent[]): Promise<LotCostEvent[]> => {
    await ensureOfflineReady()
    if (logs.length === 0) return []

    const timestamp = nowIso()
    const normalizedLogs = logs.map((log) =>
      normalizeLotCostEventRecord({
        candidate: log,
        createdAt: typeof log.createdAt === 'string' ? log.createdAt : timestamp,
        updatedAt: timestamp
      })
    )

    await dexieLotCostEventRepo.upsertMany(normalizedLogs)
    await enqueueEntityChanges(
      normalizedLogs.map((log) => ({
        entityType: 'lotCostEvent' as const,
        op: 'upsert' as const,
        entityId: log.id,
        payload: buildOutboxPayload(log)
      }))
    )

    return normalizedLogs
  },

  update: async (
    id: string,
    patch: LotCostEventUpdatePayload
  ): Promise<LotCostEventMutationResult> => {
    await ensureOfflineReady()

    const existing = await dexieLotCostEventRepo.getById(id)
    if (!existing) throw new Error('Lot cost event not found')

    const nextSourceEventType = Object.prototype.hasOwnProperty.call(patch, 'sourceEventType')
      ? toOptionalTrimmed(patch.sourceEventType ?? undefined)
      : existing.sourceEventType
    const nextSourceEventId = Object.prototype.hasOwnProperty.call(patch, 'sourceEventId')
      ? toOptionalTrimmed(patch.sourceEventId ?? undefined)
      : existing.sourceEventId
    if ((nextSourceEventType && !nextSourceEventId) || (!nextSourceEventType && nextSourceEventId)) {
      throw new Error('sourceEventType and sourceEventId must be provided together')
    }

    const next: LotCostEvent = {
      ...existing,
      productionUnitId: Object.prototype.hasOwnProperty.call(patch, 'productionUnitId')
        ? toOptionalTrimmed(patch.productionUnitId ?? undefined)
        : existing.productionUnitId,
      recordedAt: patch.recordedAt ? normalizeRecordedAt(patch.recordedAt) : existing.recordedAt,
      expenseNature: Object.prototype.hasOwnProperty.call(patch, 'expenseNature')
        ? normalizeExpenseNature(patch.expenseNature)
        : existing.expenseNature,
      costClassification: Object.prototype.hasOwnProperty.call(patch, 'costClassification')
        ? normalizeCostClassification(patch.costClassification)
        : existing.costClassification,
      amountCents: Object.prototype.hasOwnProperty.call(patch, 'amountCents')
        ? normalizeAmountCents(patch.amountCents)
        : existing.amountCents,
      currency: Object.prototype.hasOwnProperty.call(patch, 'currency')
        ? normalizeCurrency(patch.currency)
        : existing.currency,
      sourceEventType: nextSourceEventType,
      sourceEventId: nextSourceEventId,
      notes: Object.prototype.hasOwnProperty.call(patch, 'notes')
        ? toOptionalTrimmed(patch.notes ?? undefined)
        : existing.notes,
      metadata: Object.prototype.hasOwnProperty.call(patch, 'metadata')
        ? (patch.metadata ?? undefined)
        : existing.metadata,
      updatedAt: nowIso()
    }

    await dexieLotCostEventRepo.upsert(next)
    await enqueueEntityChange({
      entityType: 'lotCostEvent',
      op: 'upsert',
      entityId: next.id,
      payload: buildOutboxPayload(next)
    })

    return buildQueuedEntityMutationResult(next, {
      entityType: 'lotCostEvent',
      entityId: next.id
    })
  },

  delete: async (id: string): Promise<LotCostEventDeleteResult> => {
    await ensureOfflineReady()

    const existing = await dexieLotCostEventRepo.getById(id)

    await dexieLotCostEventRepo.delete(id)
    await enqueueEntityChange({
      entityType: 'lotCostEvent',
      op: 'delete',
      entityId: id,
      payload: existing ? buildOutboxPayload(existing) : undefined
    })

    return buildQueuedEntityMutationResult(undefined, {
      entityType: 'lotCostEvent',
      entityId: id
    })
  }
}
